import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Typography, Stack, Divider, Skeleton,
} from '@mui/material';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import axios from 'axios';
import { useGrowSetup } from '../../GrowSetupContext';
import { AuthContext }  from '../../components/signin/AuthContext';
import ActorConfigCard  from '../../components/ActorConfigCard';

export default function ActorConfigPage() {
  const { grow_setup } = useGrowSetup();
  const { token }      = useContext(AuthContext);
  const [actors,  setActors]  = useState([]);
  const [loading, setLoading] = useState(true);

  const getActorId   = useCallback(a => a?.actor_id ?? a?.id ?? a?._id, []);
  const getChannel   = useCallback(a => a?.dimmer ?? a?.channel ?? null, []);
  const setChannelOn = useCallback((a, ch) => {
    if ('dimmer'  in a) return { ...a, dimmer: ch };
    if ('channel' in a) return { ...a, channel: ch };
    return { ...a, dimmer: ch };
  }, []);

  const fetchData = useCallback(async () => {
    if (!grow_setup) return;
    setLoading(true);
    try {
      const res = await axios.post(`/get_actor_config/${grow_setup}`, { hello: 'world' }, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });
      setActors(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [grow_setup]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const portOwner = useMemo(() => {
    const map = {};
    for (const a of actors) {
      const ch = getChannel(a);
      if (ch === 1 || ch === 2 || ch === 3) map[ch] = a;
    }
    return map;
  }, [actors, getChannel]);

  const onTryChangeChannel = useCallback((actorId, desiredChannel) => {
    if (desiredChannel === 0 || ![1,2,3].includes(desiredChannel)) {
      setActors(prev => prev.map(a => getActorId(a) === actorId ? setChannelOn(a, desiredChannel) : a));
      return { ok: true };
    }
    const me      = actors.find(a => getActorId(a) === actorId);
    const current = getChannel(me);
    if (current === desiredChannel) return { ok: true };
    const owner = actors.find(a => getChannel(a) === desiredChannel && getActorId(a) !== actorId);
    if (owner) {
      window.alert(`Port ${desiredChannel} ist bereits belegt.`);
      return { ok: false, conflictWith: owner };
    }
    setActors(prev => prev.map(a => getActorId(a) === actorId ? setChannelOn(a, desiredChannel) : a));
    return { ok: true };
  }, [actors, getActorId, getChannel, setChannelOn]);

  if (loading) return (
    <Grid container spacing={2}>
      {[0,1,2,3].map(i => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
          <Skeleton variant="rounded" height={200} sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '16px' }} />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box>
      {/* Port overview */}
      <Box sx={{
        mb: 3, p: 2,
        borderRadius: '14px',
        background: 'rgba(15,28,15,0.6)',
        border: '1px solid rgba(118,196,66,0.12)',
        backdropFilter: 'blur(10px)',
        animation: 'fadeSlideUpCard 0.4s both',
      }}>
        <Typography variant="overline" sx={{ color: '#76c442', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.68rem' }}>
          Port-Belegung
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
          {[1,2,3].map(p => (
            <Box key={p} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: '8px',
                background: portOwner[p] ? 'rgba(118,196,66,0.15)' : 'rgba(255,255,255,0.04)',
                border: portOwner[p] ? '1px solid rgba(118,196,66,0.4)' : '1px solid rgba(255,255,255,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: portOwner[p] ? '#76c442' : 'rgba(255,255,255,0.3)' }}>{p}</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: portOwner[p] ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)' }}>
                {portOwner[p] ? (portOwner[p].name || `Aktor ${getActorId(portOwner[p])}`) : 'Frei'}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      <Divider sx={{ mb: 3, borderColor: 'rgba(118,196,66,0.1)' }} />

      {actors.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <TuneRoundedIcon sx={{ fontSize: 56, color: 'rgba(118,196,66,0.2)', mb: 2 }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>Keine Aktoren konfiguriert.</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {actors.map((actor, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={getActorId(actor) ?? i}>
              <Box sx={{
                animation: `fadeSlideUpCard 0.5s cubic-bezier(0.22,1,0.36,1) ${i * 60}ms both`,
                '& .MuiCard-root': {
                  background: 'rgba(15,28,15,0.75) !important',
                  border: '1px solid rgba(118,196,66,0.12) !important',
                  backdropFilter: 'blur(16px)',
                  borderRadius: '14px !important',
                },
              }}>
                <ActorConfigCard
                  {...actor}
                  token={token}
                  grow_setup={grow_setup}
                  onTryChangeChannel={onTryChangeChannel}
                  portOwner={portOwner}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      <style>{`
        @keyframes fadeSlideUpCard {
          from { opacity:0; transform:translateY(20px); }
          to   { opacity:1; transform:translateY(0);    }
        }
      `}</style>
    </Box>
  );
}
